import { useEffect, useState } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Plus, Trash2, Archive, ArchiveRestore, Layers, Users, Lock, Unlock, Pencil,
} from "lucide-react";

const TIER_COLOR = {
  STANDARD:      "bg-slate-100 text-slate-700 border-slate-300",
  INSTITUTIONAL: "bg-blue-100 text-blue-900 border-blue-300",
  HNW:           "bg-amber-100 text-amber-900 border-amber-300",
  SOVEREIGN:     "bg-purple-100 text-purple-900 border-purple-300",
};

const EMPTY_DEAL = {
  title: "", summary: "", min_tier: "INSTITUTIONAL",
  pitch_deck_url: "", disclosure_text: "",
  sector: "", geography: "", target_raise: "",
};

const AdminDealFlowPage = () => {
  const [deals, setDeals] = useState([]);
  const [tierOptions, setTierOptions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(null); // {id?, ...} for create/edit
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [d, u] = await Promise.all([
        api.get("/dealflow/admin/deals", { params: { include_archived: true } }),
        api.get("/dealflow/admin/users"),
      ]);
      setDeals(d.data.deals || []);
      setTierOptions(d.data.tier_options || []);
      setUsers(u.data.users || []);
    } catch (e) {
      toast.error("Failed to load Deal Room admin");
    } finally {
      setLoading(false);
    }
  };

  const saveDeal = async () => {
    if (!editing.title?.trim() || !editing.summary?.trim()) {
      toast.error("Title and summary are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...editing };
      // strip empty optional fields
      ["pitch_deck_url", "sector", "geography", "target_raise", "disclosure_text"].forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      if (editing.id) {
        await api.put(`/dealflow/admin/deals/${editing.id}`, payload);
        toast.success("Deal updated");
      } else {
        await api.post("/dealflow/admin/deals", payload);
        toast.success("Deal published");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const archiveDeal = async (deal) => {
    try {
      await api.put(`/dealflow/admin/deals/${deal.id}`, { archived: !deal.archived });
      toast.success(deal.archived ? "Restored" : "Archived");
      load();
    } catch {
      toast.error("Failed to update");
    }
  };

  const deleteDeal = async (deal) => {
    if (!window.confirm(`Permanently delete "${deal.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/dealflow/admin/deals/${deal.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const toggleFunded = async (u) => {
    try {
      await api.post(`/dealflow/admin/users/${u.id}/funded`, {
        funded: !u.asset_manager_funded,
        note: "admin manual toggle",
      });
      toast.success(`${u.email} ${!u.asset_manager_funded ? "unlocked" : "locked"}`);
      load();
    } catch {
      toast.error("Failed to toggle");
    }
  };

  const setUserTier = async (u, tier) => {
    try {
      await api.post(`/dealflow/admin/users/${u.id}/tier`, { tier });
      toast.success(`Tier set: ${tier}`);
      load();
    } catch {
      toast.error("Failed to set tier");
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="admin-dealflow-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="text-gold text-xs tracking-[0.3em] uppercase mb-1">
              Deal Room
            </p>
            <h1 className="text-2xl sm:text-3xl font-serif text-cream">
              Deal Room Operations
            </h1>
          </div>
          <Button
            onClick={() => setEditing({ ...EMPTY_DEAL })}
            className="bg-gold hover:bg-gold/90 text-navy-dark"
            data-testid="admin-dealflow-new-deal"
          >
            <Plus className="w-4 h-4 mr-2" /> New Deal
          </Button>
        </div>

        <Tabs defaultValue="deals" className="w-full">
          <TabsList className="bg-obsidian/60 border border-gold/15">
            <TabsTrigger value="deals" data-testid="admin-dealflow-tab-deals">
              <Layers className="w-4 h-4 mr-2" /> Deals ({deals.filter(d => !d.archived).length})
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-dealflow-tab-users">
              <Users className="w-4 h-4 mr-2" /> Funded Users ({users.filter(u => u.asset_manager_funded).length}/{users.length})
            </TabsTrigger>
          </TabsList>

          {/* Deals tab */}
          <TabsContent value="deals" className="mt-5">
            {loading ? (
              <p className="text-cream/60 text-sm py-10 text-center">Loading…</p>
            ) : deals.length === 0 ? (
              <Card className="border-gold/20 bg-obsidian/60">
                <CardContent className="p-10 text-center text-cream/60">
                  No deals published yet. Click "New Deal" to create your first opportunity.
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {deals.map((d) => (
                  <Card key={d.id} className={`border-gold/20 bg-obsidian/60 ${d.archived ? "opacity-50" : ""}`} data-testid={`admin-deal-${d.id}`}>
                    <CardContent className="p-5 flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className="text-cream font-medium">{d.title}</h3>
                          <Badge className={`border ${TIER_COLOR[d.min_tier] || ""}`}>
                            {d.min_tier_label}
                          </Badge>
                          {d.archived && <Badge variant="outline" className="border-cream/30 text-cream/60">Archived</Badge>}
                        </div>
                        <p className="text-cream/65 text-sm mb-2">{d.summary}</p>
                        <div className="flex gap-3 text-xs text-cream/45 flex-wrap">
                          {d.sector && <span>{d.sector}</span>}
                          {d.geography && <span>{d.geography}</span>}
                          {d.target_raise && <span>{d.target_raise}</span>}
                          {d.pitch_deck_url && (
                            <a href={d.pitch_deck_url} target="_blank" rel="noopener noreferrer" className="text-gold/70 hover:text-gold underline">deck link</a>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm" variant="outline"
                          className="border-gold/30 text-gold hover:bg-gold/10"
                          onClick={() => setEditing({ ...d })}
                          data-testid={`admin-deal-edit-${d.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          className="border-cream/30 text-cream hover:bg-cream/5"
                          onClick={() => archiveDeal(d)}
                          data-testid={`admin-deal-archive-${d.id}`}
                        >
                          {d.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          className="border-red-400/40 text-red-400 hover:bg-red-500/10"
                          onClick={() => deleteDeal(d)}
                          data-testid={`admin-deal-delete-${d.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Users tab */}
          <TabsContent value="users" className="mt-5">
            {loading ? (
              <p className="text-cream/60 text-sm py-10 text-center">Loading…</p>
            ) : users.length === 0 ? (
              <Card className="border-gold/20 bg-obsidian/60">
                <CardContent className="p-10 text-center text-cream/60">
                  No client users yet.
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {users.map((u) => (
                  <Card key={u.id} className="border-gold/15 bg-obsidian/50" data-testid={`admin-user-${u.id}`}>
                    <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-[200px]">
                        <p className="text-cream text-sm font-medium">{u.full_name || u.email}</p>
                        <p className="text-cream/50 text-xs">{u.email}</p>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="text-[10px] tracking-widest uppercase text-cream/40">Tier</span>
                        <Select
                          value={u.dealflow_tier}
                          onValueChange={(v) => setUserTier(u, v)}
                        >
                          <SelectTrigger className="w-[170px] h-9 bg-obsidian border-gold/20 text-cream text-xs" data-testid={`admin-user-tier-${u.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {tierOptions.map((t) => (
                              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {u.dealflow_tier_override && (
                          <Badge variant="outline" className="border-amber-400/40 text-amber-300 text-[10px]">override</Badge>
                        )}
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-[10px] tracking-widest uppercase text-cream/40">
                            {u.asset_manager_funded ? "Unlocked" : "Locked"}
                          </p>
                          {u.asset_manager_funded_source && (
                            <p className="text-[9px] text-cream/35 mt-0.5">{u.asset_manager_funded_source}</p>
                          )}
                        </div>
                        <Switch
                          checked={u.asset_manager_funded}
                          onCheckedChange={() => toggleFunded(u)}
                          data-testid={`admin-user-funded-${u.id}`}
                        />
                        {u.asset_manager_funded ? <Unlock className="w-4 h-4 text-emerald-400" /> : <Lock className="w-4 h-4 text-cream/40" />}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Compliance footer */}
        <p className="text-center text-cream/40 text-[10px] tracking-widest uppercase mt-12">
          EchoSense Capital Club acts solely as an Introducer. No investment advice is provided.
        </p>
      </div>

      {/* Editor Modal */}
      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => !saving && setEditing(null)}
          data-testid="admin-dealflow-editor"
        >
          <div
            className="bg-obsidian border border-gold/30 rounded-lg max-w-2xl w-full p-7 shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-cream text-xl font-serif mb-5">
              {editing.id ? "Edit Deal" : "Publish New Deal"}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="text-cream/70 text-xs uppercase tracking-widest mb-1.5 block">Title *</label>
                <Input
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  className="bg-black/40 border-gold/20 text-cream"
                  data-testid="admin-deal-title"
                />
              </div>

              <div>
                <label className="text-cream/70 text-xs uppercase tracking-widest mb-1.5 block">Summary *</label>
                <Textarea
                  value={editing.summary}
                  onChange={(e) => setEditing({ ...editing, summary: e.target.value })}
                  rows={3}
                  className="bg-black/40 border-gold/20 text-cream"
                  data-testid="admin-deal-summary"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-cream/70 text-xs uppercase tracking-widest mb-1.5 block">Minimum Tier *</label>
                  <Select
                    value={editing.min_tier}
                    onValueChange={(v) => setEditing({ ...editing, min_tier: v })}
                  >
                    <SelectTrigger className="bg-black/40 border-gold/20 text-cream" data-testid="admin-deal-tier">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {tierOptions.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-cream/70 text-xs uppercase tracking-widest mb-1.5 block">Target Raise</label>
                  <Input
                    value={editing.target_raise || ""}
                    onChange={(e) => setEditing({ ...editing, target_raise: e.target.value })}
                    placeholder="$25M Series B"
                    className="bg-black/40 border-gold/20 text-cream"
                    data-testid="admin-deal-target-raise"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-cream/70 text-xs uppercase tracking-widest mb-1.5 block">Sector</label>
                  <Input
                    value={editing.sector || ""}
                    onChange={(e) => setEditing({ ...editing, sector: e.target.value })}
                    placeholder="Maritime / FinTech / Energy"
                    className="bg-black/40 border-gold/20 text-cream"
                  />
                </div>
                <div>
                  <label className="text-cream/70 text-xs uppercase tracking-widest mb-1.5 block">Geography</label>
                  <Input
                    value={editing.geography || ""}
                    onChange={(e) => setEditing({ ...editing, geography: e.target.value })}
                    placeholder="EU / US / APAC"
                    className="bg-black/40 border-gold/20 text-cream"
                  />
                </div>
              </div>

              <div>
                <label className="text-cream/70 text-xs uppercase tracking-widest mb-1.5 block">
                  Pitch Deck URL <span className="text-cream/40 normal-case tracking-normal">(OneDrive / SharePoint share link)</span>
                </label>
                <Input
                  value={editing.pitch_deck_url || ""}
                  onChange={(e) => setEditing({ ...editing, pitch_deck_url: e.target.value })}
                  placeholder="https://1drv.ms/p/..."
                  className="bg-black/40 border-gold/20 text-cream"
                  data-testid="admin-deal-deck-url"
                />
              </div>

              <div>
                <label className="text-cream/70 text-xs uppercase tracking-widest mb-1.5 block">
                  Compliance Disclosure <span className="text-cream/40 normal-case tracking-normal">(leave blank for default)</span>
                </label>
                <Textarea
                  value={editing.disclosure_text || ""}
                  onChange={(e) => setEditing({ ...editing, disclosure_text: e.target.value })}
                  rows={4}
                  className="bg-black/40 border-gold/20 text-cream text-xs"
                  placeholder="Standard EchoSense Introducer-only notice applies."
                />
              </div>
            </div>

            <div className="flex gap-3 mt-7">
              <Button
                variant="outline"
                onClick={() => setEditing(null)}
                disabled={saving}
                className="flex-1 border-cream/20 text-cream hover:bg-cream/5"
                data-testid="admin-deal-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={saveDeal}
                disabled={saving}
                className="flex-1 bg-gold hover:bg-gold/90 text-navy-dark font-semibold"
                data-testid="admin-deal-save"
              >
                {saving ? "Saving…" : (editing.id ? "Update Deal" : "Publish Deal")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default AdminDealFlowPage;
